import { useEffect, useMemo, useState } from "react";

/**
 * The formats a backfill pass can fill with.
 *
 * Ordered by what actually works today: the two that need no footage first, the
 * video ones last and labelled as such, because scheduling those fills a week
 * with posts that cannot be sent until a reel exists.
 */
/**
 * CHANNEL FIRST, THEN WHAT TO POST ON IT.
 *
 * This used to be one flat row of ten "channel · format" chips, which answered
 * "which formats" and never "which channels" — so filling Instagram and
 * Facebook meant finding their chips among TikTok's and YouTube's, and leaving
 * everything unticked silently meant every format on every channel. Picking the
 * channel is the decision an operator actually makes first; the formats are a
 * refinement inside it.
 *
 * These are the pairs the format matrix actually has. Anything not listed here
 * is not a post this platform makes.
 */
type Channel = "facebook" | "instagram" | "youtube" | "tiktok";

const CHANNELS: { id: Channel; label: string }[] = [
  { id: "instagram", label: "אינסטגרם" },
  { id: "facebook", label: "פייסבוק" },
  { id: "tiktok", label: "טיקטוק" },
  { id: "youtube", label: "יוטיוב" },
];

const FORMAT_CHOICES: { id: string; channel: Channel; label: string; hint: string; video?: boolean }[] = [
  { id: "instagram:carousel", channel: "instagram", label: "קרוסלה", hint: "שיעור השמירות הגבוה ביותר, ולא דורשת וידאו." },
  { id: "instagram:story", channel: "instagram", label: "סטורי", hint: "עד ארבעה פריימים מהדק." },
  { id: "instagram:reel", channel: "instagram", label: "ריל", hint: "דורש וידאו.", video: true },
  { id: "facebook:feed_link", channel: "facebook", label: "פוסט קישור", hint: "הפוסט היחיד שהקישור בו לחיץ. דורש תמונה ראשית." },
  { id: "facebook:story", channel: "facebook", label: "סטורי", hint: "עד ארבעה פריימים מהדק." },
  { id: "facebook:reel", channel: "facebook", label: "ריל", hint: "דורש וידאו.", video: true },
  { id: "tiktok:photo_post", channel: "tiktok", label: "תמונות", hint: "הדק מקוצר, בלי וידאו." },
  { id: "tiktok:video", channel: "tiktok", label: "וידאו", hint: "דורש וידאו.", video: true },
  { id: "youtube:short", channel: "youtube", label: "שורטס", hint: "דורש וידאו.", video: true },
];

/** What a channel gets when it is ticked: its formats that need no footage. */
const DEFAULT_FORMATS_FOR = (c: Channel) =>
  FORMAT_CHOICES.filter((f) => f.channel === c && !f.video).map((f) => f.id);
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle, Check } from "lucide-react";
import { api, type BackfillCandidateView, type BackfillPreviewView, type BackfillJobView, type RemovedFormatView, type BackfillSourceView } from "../../lib/api.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * Filling the calendar forward from articles that are already published.
 *
 * TWO STEPS, ALWAYS, AND THE PREVIEW IS NOT SKIPPABLE. This spends money — a
 * pack is about $0.165 — and it writes dates onto posts that will go out under
 * the brand. Both are bad things to discover afterwards, so the dry run runs
 * first and the apply button does not exist until its plan is on screen.
 */

const TZ = "Asia/Jerusalem";

/**
 * The server's reasons stay English — they are also log lines, and a log in
 * Hebrew is worse to grep. The operator's screen is Hebrew, so they are
 * translated here, with the raw string as the fallback so a new reason shows
 * up as itself rather than vanishing.
 */
function reasonHe(reason: string): string {
  if (reason.startsWith("operator-owned")) return "עמוד בבעלות העורך — לא לאוטומציה";
  if (reason.includes("no format is possible")) return "אין תמונה או וידאו, אז אין פורמט אפשרי";
  if (reason.includes("no article produced")) return "לא הופק מאמר";
  if (reason.includes("keyword not found")) return "מילת המפתח לא נמצאה";
  if (reason.includes("no pack to schedule")) return "אין חבילה לתזמן";
  return reason;
}
/** "אינסטגרם · קרוסלה", from the same table the format chips are drawn from. */
function pairLabel(platform: string, format: string): string {
  const channel = CHANNELS.find((c) => c.id === platform)?.label ?? platform;
  const choice = FORMAT_CHOICES.find((f) => f.id === `${platform}:${format}`)?.label ?? format;
  return `${channel} · ${choice}`;
}

/**
 * Formats this pass asked for that somebody had already taken off the calendar.
 *
 * This block is the entire reason it exists: picking a format whose only row is
 * `skipped` used to produce a plan with zero posts, no cost, no skip reason and
 * no card — three layers of silence that read as a dead button (an Instagram
 * carousel removed by hand two days earlier, found 2026-09-16).
 *
 * The fill does NOT put it back by itself. Reversing an operator's decision
 * without asking is worse than declining to, so the undo is a button, here,
 * where the question comes up.
 */
function RemovedByHand({ items, onRestored }: { items: RemovedFormatView[]; onRestored: () => void }) {
  const restore = useMutation({
    mutationFn: (variantId: number) => api.restoreSocialVariant(variantId),
    onSuccess: onRestored,
  });
  return (
    <div className="space-y-1 rounded border border-amber-300 bg-amber-50/60 p-2">
      <p className="font-medium text-amber-900">
        {items.length === 1
          ? "פורמט אחד שביקשתם הוסר מהיומן ידנית, ולכן לא יתוזמן"
          : `${items.length} פורמטים שביקשתם הוסרו מהיומן ידנית, ולכן לא יתוזמנו`}
      </p>
      <ul className="space-y-1">
        {items.map((r) => (
          <li key={r.variantId} className="flex flex-wrap items-baseline gap-2">
            <span className="truncate">{r.label}</span>
            <span className="text-muted">{pairLabel(r.platform, r.format)}</span>
            <span className="text-muted">· הוסר ב־{fmtMoment(r.removedAt)}</span>
            <button
              onClick={() => restore.mutate(r.variantId)}
              disabled={restore.isPending}
              className="rounded border border-amber-700 px-1.5 py-0.5 text-amber-900 disabled:opacity-40"
            >
              החזרה ליומן
            </button>
          </li>
        ))}
      </ul>
      <p className="text-muted">
        החזרה מחזירה את הפוסט לטיוטה בלי תאריך. אחרי זה צריך תצוגה מקדימה שוב כדי לשבץ אותו.
      </p>
      {restore.isError && <p className="text-danger">{(restore.error as Error).message}</p>}
    </div>
  );
}

const fmtDay = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", { timeZone: TZ, day: "2-digit", month: "2-digit" }).format(new Date(iso));
const fmtMoment = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", { timeZone: TZ, weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));

/** Which list a row came from, for the mixed listing. */
const SOURCE_LABEL: Record<string, string> = {
  platform: "פלטפורמה",
  "sanity-blog": "ארכיון",
  "sanity-treatment": "שיטת טיפול",
  spotlight: "ספוטלייט",
};

/**
 * Opening the fill from the content radar: one article, its source, and the
 * moment it should go out around. Everything stays editable in the dialog.
 */
export interface BackfillPreset {
  source: "platform" | "sanity-blog";
  ids: string[];
  /** The fill starts looking for free moments here instead of now. */
  startIso: string;
  note: string;
}

export function BackfillDialog({ onClose, onDone, preset }: { onClose: () => void; onDone: () => void; preset?: BackfillPreset }) {
  const dismiss = useDismiss(onClose);
  const [selected, setSelected] = useState<Set<string>>(new Set(preset?.ids ?? []));
  // An article a day by default: every picked format of an article goes out
  // together, so one day per article is the natural cadence.
  const [spacingDays, setSpacingDays] = useState(1);
  const [generateMissing, setGenerateMissing] = useState(true);
  // Which formats this pass fills. The calendar gets filled in passes, not in
  // one shot: "carousels, nothing else" is the pass that works today — highest
  // save rate, and the only format that needs no footage. Scheduling the reels
  // beside it fills the week with posts that cannot send because no video
  // exists yet. Empty set = every format an article supports, the old behaviour.
  const [formats, setFormats] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Set<Channel>>(new Set());

  /**
   * Ticking a channel turns on its footage-free formats; unticking it removes
   * all of its formats. A channel with no formats left is unticked with it, so
   * the two can never disagree about what will be scheduled.
   */
  const toggleChannel = (c: Channel) => {
    invalidate();
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        next.delete(c);
        setFormats((f) => new Set([...f].filter((id) => !id.startsWith(`${c}:`))));
      } else {
        next.add(c);
        setFormats((f) => new Set([...f, ...DEFAULT_FORMATS_FOR(c)]));
      }
      return next;
    });
  };

  const toggleFormat = (id: string) => {
    invalidate();
    setFormats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      const c = id.split(":")[0] as Channel;
      if (![...next].some((x) => x.startsWith(`${c}:`))) {
        setChannels((ch) => { const n = new Set(ch); n.delete(c); return n; });
      }
      return next;
    });
  };
  const [preview, setPreview] = useState<BackfillPreviewView | null>(null);
  /**
   * The background run, polled. It replaces the old synchronous `result`: a fill
   * takes minutes, and the request used to wait for it until the gateway gave up
   * with a 504 — after which the button looked failed and got pressed again.
   */
  const [job, setJob] = useState<BackfillJobView | null>(null);
  const running = job?.status === "running";
  const result = job?.status === "done" ? job.result : null;

  // RE-ATTACH ON OPEN. If a fill is already running — started a minute ago, from
  // a dialog that was closed, or before a refresh — show it instead of offering
  // a button that would start a second one.
  useEffect(() => {
    void api.backfillStatus().then((r) => {
      if (r.job?.status === "running") setJob(r.job);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      void api.backfillStatus().then((r) => { if (r.job) setJob(r.job); }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [running]);

  /**
   * Where the posts come from, and `all` is the default: the platform's own
   * articles, the live blog posts written before the platform existed, and the
   * treatment-method pages, in ONE list you can search and pick across.
   *
   * It used to be one source at a time, switching cleared the selection, and
   * the default was the platform — so a calendar filled by hand was made
   * entirely of the newest articles while 43 archive posts and 67 method pages
   * sat one deliberate click away. The three mix freely; SPOTLIGHT DOES NOT,
   * because a run carrying a campaign is forced to a 7-day spacing that would
   * stretch every article beside it a week apart. Picking it clears the rest.
   */
  const [source, setSource] = useState<BackfillSourceView | "all">(preset?.source ?? "all");
  const [query, setQuery] = useState("");
  const candidates = useQuery({
    queryKey: ["backfill-candidates", source],
    queryFn: () => api.backfillCandidates(source),
  });

  const all = candidates.data?.candidates ?? [];
  // Search is a plain case-insensitive substring on the title. Hebrew has no
  // useful stemming to do here and a fuzzy match would hide the article you
  // typed the name of behind four that merely rhyme with it.
  const needle = query.trim().toLowerCase();
  const list = useMemo(
    () => (needle ? all.filter((c) => c.label.toLowerCase().includes(needle)) : all),
    [all, needle],
  );
  const selectable = useMemo(() => list.filter((c) => !c.blocked), [list]);
  // Something chosen and then searched away is still going out. Counting only
  // what is on screen would make the button lie about what apply will do.
  const hiddenSelected = selected.size - list.filter((c) => selected.has(c.keywordId)).length;

  const body = () => ({
    keywordIds: [...selected],
    spacingDays,
    generateMissing,
    // ALWAYS explicit. An empty set used to mean every format on every channel,
    // which is the most expensive possible reading of "I have not chosen yet".
    formats: [...formats],
    ...(preset?.startIso && Date.parse(preset.startIso) > Date.now() ? { startIso: preset.startIso } : {}),
  });

  // The preview is invalidated by ANY change to the inputs. A plan on screen
  // that no longer matches the checkboxes is worse than no plan.
  const invalidate = () => { if (running) return; setPreview(null); setJob(null); };

  const dryRun = useMutation({
    mutationFn: () => api.backfill(body()) as Promise<BackfillPreviewView>,
    onSuccess: (r) => setPreview(r),
  });

  const apply = useMutation({
    mutationFn: () => api.backfill({ ...body(), apply: true }) as Promise<{ job: BackfillJobView }>,
    onSuccess: (r) => setJob(r.job),
    // A 409 means a fill is ALREADY running. That is not an error to show next
    // to the button — it is the run to show instead of the button.
    onError: () => {
      void api.backfillStatus().then((r) => { if (r.job?.status === "running") setJob(r.job); }).catch(() => {});
    },
  });

  const toggle = (id: string) => {
    invalidate();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Spotlight packs are built free from their campaign, so they never need the "create packs" tick.
  const needGen = preview?.needGeneration.filter((n) => !n.keywordId.startsWith("spotlight:")).length ?? 0;

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">מילוי היומן ממאמרים שכבר פורסמו</h2>
          <button onClick={onClose} className="text-sm text-muted hover:text-ink">סגירה</button>
        </header>

        <div className="grow overflow-auto p-4">
          {preset && (
            <p className="mb-3 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">{preset.note}</p>
          )}
          {candidates.isPending && <p className="flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> טוען מאמרים…</p>}

          {candidates.data && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
                <button
                  onClick={() => { invalidate(); setSelected((prev) => new Set([...prev, ...selectable.map((c) => c.keywordId)])); }}
                  className="rounded border border-line px-2 py-1 hover:bg-canvas"
                >{needle ? `בחירת התוצאות (${selectable.length})` : `בחירת הכל (${selectable.length})`}</button>
                <button
                  onClick={() => { invalidate(); setSelected(new Set()); }}
                  className="rounded border border-line px-2 py-1 hover:bg-canvas"
                >ניקוי</button>

                <label className="flex items-center gap-1.5">
                  מרווח בין מאמרים
                  <input
                    type="number" min={1} max={30} value={spacingDays}
                    onChange={(e) => { invalidate(); setSpacingDays(Number(e.target.value) || 1); }}
                    className="w-14 rounded border border-line px-1.5 py-1"
                  />
                  ימים
                </label>

                {/* Channel first, then the posts on it. */}
                <div className="w-full space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="w-14 shrink-0">ערוצים</span>
                    {CHANNELS.map((c) => (
                      <label
                        key={c.id}
                        className={`flex cursor-pointer items-center gap-1 rounded border px-2 py-1 ${
                          channels.has(c.id) ? "border-brand-700 bg-brand-50 text-brand-800" : "border-line hover:bg-canvas"
                        }`}
                      >
                        <input type="checkbox" checked={channels.has(c.id)} onChange={() => toggleChannel(c.id)} />
                        {c.label}
                      </label>
                    ))}
                    {channels.size === 0 && <span className="text-amber-800">בחרו לפחות ערוץ אחד</span>}
                  </div>

                  {CHANNELS.filter((c) => channels.has(c.id)).map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center gap-1.5 ps-14">
                      <span className="w-16 shrink-0 text-muted">{c.label}:</span>
                      {FORMAT_CHOICES.filter((f) => f.channel === c.id).map((f) => (
                        <button
                          key={f.id}
                          onClick={() => toggleFormat(f.id)}
                          title={f.hint}
                          className={`rounded border px-2 py-1 ${
                            formats.has(f.id)
                              ? "border-brand-700 bg-brand-50 text-brand-800"
                              : f.video
                                ? "border-line text-muted hover:bg-canvas"
                                : "border-line hover:bg-canvas"
                          }`}
                        >
                          {f.label}
                          {/* Marked, not hidden. Choosing one is legitimate; not
                              knowing it fills the week with unsendable posts is not. */}
                          {f.video && <span className="ms-1 opacity-60">·וידאו</span>}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>

                <label className="flex items-center gap-1.5" title="מאמר בלי חבילה אין מה לתזמן בו. יצירה עולה כסף.">
                  <input
                    type="checkbox" checked={generateMissing}
                    onChange={(e) => { invalidate(); setGenerateMissing(e.target.checked); }}
                  />
                  ליצור חבילה למאמרים שאין להם
                </label>
              </div>

              <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="w-14 shrink-0">מקור</span>
                {([
                  ["all", "הכל מעורבב"],
                  ["platform", "מאמרים מהפלטפורמה"],
                  ["sanity-blog", "מאמרים ישנים מסאניטי"],
                  ["sanity-treatment", "שיטות טיפול"],
                  ["spotlight", "ספוטלייט מטפלים"],
                ] as const).map(([id, text]) => (
                  <button
                    key={id}
                    onClick={() => {
                      if (running) return;
                      // Crossing the spotlight line — in either direction —
                      // clears the selection, because the two cannot travel in
                      // one run. Moving between the three content sources keeps
                      // it: that IS the mix, and losing it on every click is
                      // what made picking across them impractical.
                      if ((id === "spotlight") !== (source === "spotlight")) setSelected(new Set());
                      setSource(id); setQuery(""); invalidate();
                      // One spotlight a week — the server enforces it; the field says it.
                      if (id === "spotlight") setSpacingDays((d) => Math.max(d, 7));
                    }}
                    className={`rounded border px-2 py-1 ${source === id ? "border-brand-700 bg-brand-50 text-brand-800" : "border-line hover:bg-canvas"}`}
                  >
                    {text}
                  </button>
                ))}
              </div>

              <div className="mb-2 flex items-center gap-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="חיפוש לפי כותרת…"
                  className="w-full rounded border border-line px-2 py-1.5 text-xs"
                />
                {needle && (
                  <button onClick={() => setQuery("")} className="shrink-0 rounded border border-line px-2 py-1 text-xs hover:bg-canvas">
                    ניקוי חיפוש
                  </button>
                )}
              </div>
              {source === "all" && (
                <p className="mb-2 text-[11px] text-muted">
                  מאמרים מהפלטפורמה, מאמרים ישנים מהאתר ועמודי שיטות טיפול — רשימה אחת. אפשר לבחור מכל המקורות בריצה אחת.
                  עד 25 מסמכים מסאניטי (ישנים + שיטות) בריצה אחת.
                </p>
              )}
              {source === "spotlight" && (
                <p className="mb-2 text-[11px] text-muted">
                  קמפיינים מעורך הספוטלייט. נכנסים ליומן רק מטפלים שאישרו, שכל השדות שלהם אושרו, ושלא הופיעו בשנה האחרונה.
                  הפוסטים הם הכיתובים והקרוסלה שאושרו, בלי כתיבה מחדש ובלי עלות: פוסט+לינק וסטורי בפייסבוק, קרוסלה וסטורי באינסטגרם.
                  ספוטלייט אחד בשבוע. לפני כל שליחה ההסכמה נבדקת שוב.
                </p>
              )}
              {(source === "sanity-blog" || source === "sanity-treatment") && (
                <p className="mb-2 text-[11px] text-muted">
                  {source === "sanity-blog"
                    ? "פוסטי בלוג חיים שהפלטפורמה לא הפיקה. הטקסט והתמונות נשלפים מסאניטי בתצוגה המקדימה — כמה פורמטים אפשריים יתברר שם. בדקו את תאריך הפרסום: מאמר ישן יכול לכלול טענות שכבר לא עומדות בכללים."
                    : "עמודי השיטות באתר. הפוסטים מציגים את השיטה ומפנים לעמוד שלה. הטקסט והתמונה נשלפים מסאניטי בתצוגה המקדימה."}
                  {" "}עד 25 בריצה אחת.
                </p>
              )}

              <div className="max-h-72 overflow-auto rounded border border-line">
                {list.map((c: BackfillCandidateView) => (
                  <label
                    key={c.keywordId}
                    className={`flex items-center gap-2 border-b border-line px-2 py-1.5 text-xs last:border-0 ${
                      c.blocked ? "opacity-50" : "cursor-pointer hover:bg-canvas"
                    }`}
                  >
                    <input
                      type="checkbox" disabled={Boolean(c.blocked)}
                      checked={selected.has(c.keywordId)}
                      onChange={() => toggle(c.keywordId)}
                    />
                    <span className="grow truncate">{c.label}</span>
                    {/* In the mixed list a row is meaningless without it: an
                        article the platform wrote, an archive post and a method
                        page read the same and are handled completely differently. */}
                    {source === "all" && c.source && (
                      <span className="shrink-0 rounded bg-canvas px-1.5 text-[11px] text-muted">{SOURCE_LABEL[c.source]}</span>
                    )}
                    {c.publishedAt && c.source !== "platform" && (
                      <span className="shrink-0 text-muted">{new Date(c.publishedAt).toLocaleDateString("he-IL")}</span>
                    )}
                    <span className="shrink-0 text-muted">{c.formats < 0 ? "ייבדק בתצוגה" : `${c.formats} פורמטים`}</span>
                    {c.hasPack
                      ? <span className="shrink-0 rounded bg-emerald-50 px-1.5 text-emerald-800">יש חבילה</span>
                      : <span className="shrink-0 rounded bg-canvas px-1.5 text-muted">אין חבילה</span>}
                    {/* Blocked articles are LISTED, not hidden: an operator who
                        cannot find an article assumes a bug, where one shown
                        with its reason answers the question. */}
                    {c.blocked && <span className="shrink-0 text-[11px] text-muted">{reasonHe(c.blocked)}</span>}
                  </label>
                ))}
              </div>
            </>
          )}

          {preview && (
            <div className="mt-4 space-y-2 rounded-lg border border-line bg-canvas p-3 text-xs">
              <p className="font-medium">
                {preview.plan.slots.length} פוסטים על {preview.plan.perArticle.length} מאמרים
                {preview.plan.lastSlotAt && <> · היומן יתמלא עד {fmtDay(preview.plan.lastSlotAt)}</>}
              </p>

              {preview.estimatedCostUsd > 0 && (
                <p className="text-amber-900">
                  יווצרו {needGen} חבילות · עלות משוערת ${preview.estimatedCostUsd.toFixed(2)}
                </p>
              )}
              {/* Packs that exist without what was asked for. Said, because the
                  preview used to promise posts the apply could not make. */}
              {(preview.needCompletion?.length ?? 0) > 0 && (
                <p className={generateMissing ? "text-amber-900" : "text-danger"}>
                  {generateMissing
                    ? `יושלמו ${preview.needCompletion.reduce((a, c) => a + c.formats.length, 0)} פורמטים חסרים ב־${preview.needCompletion.length} חבילות קיימות (למשל פוסט קישור בפייסבוק)`
                    : `ל־${preview.needCompletion.length} חבילות חסרים פורמטים שביקשת, והם לא ייווצרו — סמנו "ליצור חבילה" כדי להשלים אותם.`}
                </p>
              )}
              {/* Said BEFORE the cost line and the day-by-day list: this is the
                  answer to "I picked a format and nothing happened", and it is
                  no use underneath a fold. */}
              {(preview.removedByHand?.length ?? 0) > 0 && (
                <RemovedByHand
                  items={preview.removedByHand}
                  onRestored={() => { setPreview(null); dryRun.mutate(); }}
                />
              )}
              {generateMissing === false && needGen > 0 && (
                <p className="text-amber-900">
                  {needGen} מאמרים בלי חבילה לא יתוזמנו — סמנו "ליצור חבילה" כדי לכלול אותם.
                </p>
              )}

              <p className="text-muted">
                כל מאמר יוצא ביום ובשעה אחת, בכל הערוצים והפורמטים שנבחרו. בין שני מאמרים שונים יש לפחות 3 שעות.
              </p>
              {preview.plan.moved > 0 && (
                <p>{preview.plan.moved} מאמרים עברו ליום מאוחר יותר כי היום שלהם כבר היה תפוס.</p>
              )}
              {preview.plan.busyDays.length > 0 && (
                <p className="flex items-center gap-1 text-amber-900">
                  <AlertTriangle size={12} />
                  ימים עמוסים: {preview.plan.busyDays.slice(0, 3).map((b) => `${b.day} (${b.count})`).join(", ")}
                </p>
              )}
              {preview.skipped.length > 0 && (
                <details>
                  <summary className="cursor-pointer">{preview.skipped.length} מאמרים דולגו</summary>
                  <ul className="mt-1 space-y-0.5 text-muted">
                    {preview.skipped.map((s) => <li key={s.keywordId}>{reasonHe(s.reason)}</li>)}
                  </ul>
                </details>
              )}

              <details>
                <summary className="cursor-pointer">מתי כל מאמר יוצא</summary>
                <ul className="mt-1 space-y-0.5">
                  {preview.plan.perArticle.map((a) => (
                    <li key={a.keywordId} className="flex justify-between gap-2">
                      <span className="truncate">{a.label}</span>
                      <span className="shrink-0 text-muted">
                        {fmtMoment(a.startsAt)} · {a.posts} פוסטים
                        {a.pinned && " · לפי פוסט שכבר בפוסטיז"}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          {running && job && (
            <div className="mt-4 rounded-lg border border-brand-700/30 bg-brand-50 p-3 text-xs text-brand-800">
              <p className="flex items-center gap-1.5 font-medium">
                <Loader2 size={13} className="animate-spin" />
                {job.phase === "scheduling"
                  ? "משבץ פוסטים ביומן…"
                  : `יוצר חבילה ${Math.min(job.done + 1, job.total)} מתוך ${job.total}`}
              </p>
              {job.current && <p className="mt-1 truncate">{job.current}</p>}
              <p className="mt-1 opacity-80">
                כל חבילה לוקחת כחצי דקה עד דקה. הריצה ממשיכה בשרת גם אם תסגור את החלון — כשתפתח אותו שוב היא תופיע כאן.
              </p>
            </div>
          )}

          {job?.status === "error" && (
            <p className="mt-3 text-xs text-danger">הריצה נכשלה: {job.error}</p>
          )}

          {result && (
            <div className="mt-4 rounded-lg border border-line bg-canvas p-3 text-xs">
              <p className="flex items-center gap-1.5 font-medium">
                <Check size={13} /> נוצרו {result.generated} חבילות{result.completed > 0 ? ` · הושלמו ${result.completed} פורמטים` : ""} · תוזמנו {result.rescheduled} פוסטים
                {result.costUsd > 0 && <> · עלות ${result.costUsd.toFixed(3)}</>}
              </p>
              {result.lastSlotAt && <p className="mt-1">היומן מלא עד {fmtDay(result.lastSlotAt)}.</p>}
              {/* Also on the RESULT, not only on the plan: the run finishes
                  minutes after the plan was read, and "תוזמנו 0 פוסטים" with no
                  reason beside it is exactly what this field exists to stop. */}
              {(result.removedByHand?.length ?? 0) > 0 && (
                <div className="mt-2">
                  <RemovedByHand items={result.removedByHand} onRestored={() => { setJob(null); setPreview(null); }} />
                </div>
              )}
              {result.errors.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-danger">
                  {result.errors.map((e) => <li key={e.keywordId}>{e.keywordId}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}

          {dryRun.isError && (
            <p className="mt-3 text-xs text-danger">{(dryRun.error as Error).message}</p>
          )}
          {apply.isError && !running && (
            <p className="mt-3 text-xs text-danger">{(apply.error as Error).message}</p>
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-line px-4 py-3">
          <span className="text-xs text-muted">
            {selected.size} נבחרו
            {/* A choice you then searched away is still in the run. Saying so is
                cheaper than the surprise of an apply that does more than the
                list on screen shows. */}
            {hiddenSelected > 0 && <span className="ms-1">({hiddenSelected} מחוץ לתוצאות החיפוש)</span>}
          </span>
          <span className="grow" />

          {result ? (
            <button onClick={onDone} className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white">
              סגירה ורענון היומן
            </button>
          ) : (
            <>
              <button
                onClick={() => dryRun.mutate()}
                disabled={selected.size === 0 || formats.size === 0 || dryRun.isPending || running}
                className="rounded-lg border border-line px-3 py-2 text-sm disabled:opacity-40"
              >
                {dryRun.isPending ? <Loader2 size={14} className="animate-spin" /> : "תצוגה מקדימה"}
              </button>
              {/* Only reachable once a plan is on screen. */}
              <button
                onClick={() => apply.mutate()}
                disabled={!preview || apply.isPending || running}
                title={!preview ? "צריך תצוגה מקדימה קודם." : ""}
                className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                {apply.isPending || running ? <Loader2 size={14} className="animate-spin" /> : "ביצוע"}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
